import React, { useState, useRef, useEffect } from 'react';
import { updateTickerRating } from '../services/api';

// A single click is held for this long before it's actually applied, so a
// second click arriving within the window can turn it into a double-click
// (full reset) instead. Long enough to reliably catch a real double-click,
// short enough that a genuine single click still feels immediate.
const CLICK_DELAY_MS = 250;

const StarRating = ({ tickerId, symbol, currentRating = null, onRatingChange, size = 'sm', disabled = false }) => {
  const [saving, setSaving] = useState(false);
  const pendingClickRef = useRef(null);

  // Cancel any pending single-click action if the row unmounts (e.g. the
  // table re-filters) before the delay elapses.
  useEffect(() => () => {
    if (pendingClickRef.current) {
      clearTimeout(pendingClickRef.current);
    }
  }, []);

  const saveRating = async (newRating) => {
    setSaving(true);
    try {
      const response = await updateTickerRating(tickerId, newRating);
      if (onRatingChange) {
        onRatingChange(response.data);
      }
    } catch (err) {
      console.error('Failed to update rating', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStarClick = (starNumber) => {
    if (disabled || saving) return;
    // Hold the click briefly rather than applying it immediately -- if a
    // second click follows within CLICK_DELAY_MS, handleStarDoubleClick
    // cancels this one and resets the rating instead.
    pendingClickRef.current = setTimeout(() => {
      pendingClickRef.current = null;
      // Clicking the star matching the current rating clears it; any other
      // star sets the rating directly to that number of stars.
      const newRating = starNumber === currentRating ? null : starNumber;
      saveRating(newRating);
    }, CLICK_DELAY_MS);
  };

  const handleStarDoubleClick = () => {
    if (disabled || saving) return;
    if (pendingClickRef.current) {
      clearTimeout(pendingClickRef.current);
      pendingClickRef.current = null;
    }
    saveRating(null);
  };

  const getStarIcon = (starNumber) => {
    const rating = currentRating || 0;
    const isFilled = starNumber <= rating;

    if (isFilled) {
      return 'bi-star-fill text-warning';
    } else {
      return 'bi-star text-muted';
    }
  };

  const getSizeClass = () => {
    switch (size) {
      case 'lg':
        return 'fs-5';
      case 'md':
        return 'fs-6';
      case 'sm':
      default:
        return 'fs-6';
    }
  };

  const isInteractive = !disabled && !saving;

  return (
    <div
      className="d-inline-flex align-items-center"
      style={{ opacity: saving ? 0.6 : 1 }}
      aria-label={symbol ? `${symbol} rating` : 'rating'}
    >
      {[1, 2, 3].map((star) => (
        <i
          key={star}
          className={`bi ${getStarIcon(star)} ${getSizeClass()} me-1`}
          style={{
            cursor: isInteractive ? 'pointer' : 'default',
            transition: 'all 0.2s ease'
          }}
          onClick={() => handleStarClick(star)}
          onDoubleClick={handleStarDoubleClick}
          title={isInteractive ? `Set ${star} star${star > 1 ? 's' : ''} (double-click to clear)` : ''}
        />
      ))}
    </div>
  );
};

export default StarRating;
